import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ScrollText } from "lucide-react";

const ACTION_STYLE = {
  grant_trial: "bg-[#E8A93C]/15 text-[#8a6410]",
  comp_subscription: "bg-green-100 text-green-800",
  cancel_subscription: "bg-red-100 text-red-800",
  set_role: "bg-[#24468C]/10 text-[#24468C]",
  update_setting: "bg-purple-100 text-purple-800",
};

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/audit"); setRows(data); }
    catch (e) { logger.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-audit">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><ScrollText className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Audit Log</h1>
          <p className="text-muted-foreground text-sm">Every sensitive admin action, most recent first.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh-btn"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[#24468C]" /></div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="audit-empty">No admin actions logged yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">When</th>
                  <th className="px-4 py-3 font-semibold">Actor</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold">Target</th>
                  <th className="px-4 py-3 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-zinc-100" data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3">{r.actor_email || <span className="font-mono text-xs">{r.actor_id}</span>}</td>
                    <td className="px-4 py-3"><Badge className={ACTION_STYLE[r.action] || "bg-zinc-100 text-zinc-600"}>{r.action}</Badge></td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.target || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">{r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
